/**
 * DAC profiles -- group 12 (high-end European and North American converters).
 *
 * The spread here is deliberately architectural rather than cosmetic: a
 * sign-magnitude ladder with a wideband FET analog section, two no-feedback
 * multibit designs (one valve-coupled, one transformer-coupled) running without
 * an interpolation filter, an FPGA-corrected discrete converter, two
 * conventional delta-sigma implementations with very different analog paths, and
 * a PWM "direct digital" design that has no converter chip at all. Published
 * figures are tagged MANUFACTURER_SPEC; everything else is ESTIMATED with the
 * reasoning stated in provenanceNote. Several of these manufacturers publish no
 * distortion data at all, which is said explicitly rather than papered over.
 */

import type { DacProfile } from '../../types/hardware';
import {
  ESS_FILTERS,
  FILTERS,
  HARMONIC_SHAPES,
  SIMPLE_FILTERS,
  TRANSFORMER_LINE,
  TUBE_LINE,
  frCurve,
  makeDac,
  usd,
} from '../kit';

export const DACS_12: DacProfile[] = [
  makeDac({
    mfr: 'Spectral Audio',
    model: 'SDR-4000',
    price: usd(15000, 'APPROXIMATE', 2015, 'Approximate US retail for the SDR-4000 series; Spectral sells through a restricted dealer network and list pricing varied by configuration.'),
    year: 2007,
    country: 'United States',
    architecture: 'ladder-multibit',
    core: 'Multibit sign-magnitude ladder converter with discrete wideband FET analog section',
    outputStage: 'jfet',
    dynamicRangeDb: 116,
    thdPlusNDb: -102,
    crosstalkDb: -112,
    outputVrmsSE: 2.0,
    outputImpedanceOhms: 15,
    balanced: false,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    dsd: 'none',
    filters: SIMPLE_FILTERS(),
    ladderMismatchLsb: 0.25,
    effectiveBits: 19,
    jitterPsRms: 30,
    slewRateVus: 200,
    response: frCurve(
      [[5, 0], [20, 0], [1000, 0], [20000, 0], [40000, -0.05], [80000, -0.2], [200000, -1.4], [500000, -6]],
      'ESTIMATED',
      'Wide-bandwidth analog section; corner placed well above audio per the design brief',
    ),
    confidence: 'ESTIMATED',
    accent: '#b7c9d8',
    tags: ['ladder', 'wide-bandwidth', 'high-slew', 'single-ended'],
    notes:
      'The engineering signature of this design is time-domain rather than steady-state: a ladder converter feeding a discrete FET stage whose small-signal bandwidth extends into the hundreds of kilohertz, with slew capability far beyond what audio requires. In this model that shows up as a response that is still flat where most line stages are already rolling off, and a settling behaviour that does not soften fast transient edges. Ladder element mismatch is carried as level-dependent INL, so its distortion does not collapse toward zero at low level the way a noise-shaped converter does.',
    provenanceNote:
      'Architecture and analog design philosophy from Spectral published material; the profile models the single-ended output. Spectral does not publish a full THD+N or dynamic range table for this unit, so those figures are estimates typical of well-implemented multibit ladder converters of the period. Introduction year is approximate.',
  }),

  makeDac({
    mfr: 'darTZeel',
    model: 'LHC-08 MK II',
    price: usd(42000, 'APPROXIMATE', 2022, 'Approximate US retail for the LHC-08 MK II with the digital board fitted; configuration-dependent.'),
    year: 2019,
    country: 'Switzerland',
    architecture: 'delta-sigma',
    core: 'Delta-sigma converter board integrated into the LHC-08 platform; part number not published',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 116,
    thdPlusNDb: -98,
    crosstalkDb: -104,
    outputVrmsSE: 2.0,
    outputImpedanceOhms: 50,
    balanced: false,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    maxBitDepth: 32,
    dsd: 'dsd128',
    filters: [FILTERS.sharpLinear(128), FILTERS.slowMinimum(32)],
    noiseShaperOrder: 5,
    jitterPsRms: 45,
    slewRateVus: 40,
    confidence: 'ESTIMATED',
    accent: '#e05c5c',
    tags: ['swiss', 'zero-feedback', 'bnc-50-ohm', 'integrated-platform'],
    notes:
      'Converter board sitting inside an amplifier chassis, so the analog path between conversion and the volume control is short and single-ended throughout. The house interface is a defined 50-ohm source on BNC rather than a low-impedance voltage output, which matters here because the source impedance is three orders of magnitude above a conventional op-amp output and therefore interacts with cable capacitance and the following input impedance. Distortion is modelled as low-order and single-ended-biased, consistent with a no-global-feedback class-A output rather than a corrected op-amp stage.',
    provenanceNote:
      'Topology, 50-ohm interface and platform description from darTZeel published material. darTZeel does not publish THD+N, dynamic range or converter part numbers for the digital board, so the electrical figures here are estimates typical of a well-implemented delta-sigma board with a discrete no-feedback output. Price and introduction year are approximate.',
  }),

  makeDac({
    mfr: 'Thrax Audio',
    model: 'Maximinus',
    price: usd(33000, 'APPROXIMATE', 2021, 'Approximate US retail; Thrax pricing varies by market and revision.'),
    year: 2013,
    country: 'Bulgaria',
    architecture: 'discrete-r2r',
    core: 'Discrete multibit ladder converter with valve output stage',
    outputStage: 'tube',
    dynamicRangeDb: 110,
    thdPlusNDb: -80,
    crosstalkDb: -84,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 300,
    balanced: true,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    maxBitDepth: 24,
    dsd: 'dsd256',
    filters: [FILTERS.nos(), FILTERS.sharpLinear(128)],
    defaultFilterId: 'nos',
    ladderMismatchLsb: 0.45,
    effectiveBits: 19,
    jitterPsRms: 25,
    harmonicShape: HARMONIC_SHAPES.tubeDac,
    response: TUBE_LINE(6, -0.7),
    confidence: 'ESTIMATED',
    accent: '#d98f4f',
    tags: ['ladder', 'nos', 'valve-output', 'low-jitter-clock'],
    notes:
      'Two independent error sources stack in this design and the model keeps them separate. The ladder contributes level-dependent integral nonlinearity that does not scale away at low signal levels, while the valve output stage contributes a strongly asymmetric transfer curve dominated by second harmonic that grows roughly one dB per dB of drive. Running without an interpolation filter also means the zero-order-hold sinc envelope is left in the passband, which the rate-dependent response applies rather than hiding.',
    provenanceNote:
      'Architecture, valve output stage and clocking emphasis from Thrax published descriptions. Thrax does not publish THD or dynamic range for this unit, so both figures are estimates typical of no-feedback valve-output ladder designs; the ladder INL magnitude is likewise an estimate, not a measurement.',
  }),

  makeDac({
    mfr: 'Ypsilon Electronics',
    model: 'DAC-100',
    price: usd(38000, 'APPROXIMATE', 2020, 'Approximate US retail; Ypsilon list pricing has moved substantially with exchange rates.'),
    year: 2010,
    country: 'Greece',
    architecture: 'r2r',
    core: 'Multibit ladder converter run without oversampling, custom-wound output transformers',
    outputStage: 'transformer',
    dynamicRangeDb: 106,
    thdPlusNDb: -76,
    crosstalkDb: -78,
    outputVrmsSE: 2.2,
    outputVrmsBal: 4.4,
    outputImpedanceOhms: 600,
    balanced: true,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    dsd: 'none',
    filters: [FILTERS.nos()],
    defaultFilterId: 'nos',
    ladderMismatchLsb: 0.7,
    effectiveBits: 17,
    jitterPsRms: 80,
    response: TRANSFORMER_LINE(9, 36000),
    confidence: 'ESTIMATED',
    accent: '#8c7fbf',
    tags: ['nos', 'transformer-coupled', 'no-active-output', 'single-filter'],
    notes:
      'There is no active device in the output path at all: the converter current is taken through wound transformers, so the analog transfer function is that of a magnetic component rather than a gain stage. That produces first-order roll-off at both extremes, a source impedance high enough that the following input stage becomes part of the response, and a distortion structure dominated by core hysteresis that rises at low frequency instead of being level-flat. With only a single non-oversampling reconstruction option, the sinc droop and the full image content above Nyquist are both left in place.',
    provenanceNote:
      'Non-oversampling topology, transformer output and absence of active output devices from Ypsilon published material. Ypsilon publishes no THD, dynamic range or crosstalk figures for this unit; all electrical numbers here are estimates typical of transformer-coupled non-oversampling ladder designs. Price is approximate.',
  }),

  makeDac({
    mfr: 'Wadax',
    model: 'Atlantis Reference DAC',
    price: usd(160000, 'APPROXIMATE', 2023, 'Approximate US retail for the Atlantis Reference DAC before optional external power supply and interface upgrades.'),
    year: 2019,
    country: 'Spain',
    architecture: 'fpga-custom',
    core: 'Proprietary discrete converter with FPGA-hosted musIC correction engine',
    outputStage: 'passive-iv',
    dynamicRangeDb: 124,
    thdPlusNDb: -110,
    crosstalkDb: -125,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 30,
    balanced: true,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000, 705600, 768000],
    maxBitDepth: 32,
    dsd: 'dsd256',
    filters: [
      { ...FILTERS.brickwall(1536), id: 'musIC-recon', label: 'Long-tap reconstruction with converter error correction', stopbandDb: -155, cutoffRatio: 0.95, transitionRatio: 0.04 },
      FILTERS.apodizing(256),
    ],
    defaultFilterId: 'musIC-recon',
    internalOversampling: 64,
    effectiveBits: 21,
    jitterPsRms: 8,
    confidence: 'ESTIMATED',
    accent: '#4fb3a5',
    tags: ['fpga', 'error-correction', 'passive-iv', 'flagship'],
    notes:
      'The manufacturer describes a correction engine that characterises the converter and pre-distorts the digital stream to cancel its own transfer errors, which is architecturally the opposite of the ladder designs above: instead of accepting element mismatch as a signature, the design tries to null it. Modelled consequences are a very low and largely level-independent distortion floor, a long low-ripple reconstruction filter with correspondingly extended pre- and post-ringing at the band edge, and a passive current-to-voltage conversion that adds no active-stage harmonic structure of its own. Jitter is modelled as very low, since the correction scheme depends on a stable timing reference.',
    provenanceNote:
      'Architecture, correction concept and filter length are from Wadax published material and are described here in the manufacturer voice rather than asserted as verified. Wadax does not publish a conventional THD+N and dynamic range table, so those figures are estimates consistent with the design class; price is approximate and configuration-dependent.',
  }),

  makeDac({
    mfr: 'Trinnov Audio',
    model: 'Amethyst',
    price: usd(11000, 'APPROXIMATE', 2022, 'Approximate US retail; the Amethyst is usually sold with calibration and a measurement microphone included.'),
    year: 2019,
    country: 'France',
    architecture: 'multibit-delta-sigma',
    core: 'Multibit delta-sigma converter fed from the Trinnov Optimizer DSP engine',
    outputStage: 'op-amp',
    dynamicRangeDb: 120,
    thdPlusNDb: -104,
    crosstalkDb: -112,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 47,
    balanced: true,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    dsd: 'none',
    filters: ESS_FILTERS(),
    noiseShaperOrder: 5,
    idleToneLevel: 0.1,
    jitterPsRms: 25,
    confidence: 'ESTIMATED',
    accent: '#6fa8dc',
    tags: ['dsp', 'room-correction', 'digital-volume', 'pro-lineage'],
    notes:
      'Unlike every other entry in this group the analog section is not the interesting part: signal enters a fixed high-resolution DSP path, is filtered by long correction FIRs and attenuated by a digital volume control, and only then reaches a conventional op-amp output. The audible fingerprint therefore belongs to the processing chain, not to the converter, and the correct model is a very clean, level-independent conversion with an ordinary low-impedance line output. Digital attenuation also means the effective resolution presented to the converter falls as volume is reduced, which the dynamic-range figure here describes only at unity.',
    provenanceNote:
      'Processing architecture, correction approach and input/output complement from Trinnov published material. Trinnov does not publish the converter part number and its published distortion and dynamic-range figures are not stated per output here, so the electrical values are estimates typical of a modern multibit delta-sigma stage. Price is approximate.',
  }),

  makeDac({
    mfr: 'Metronome Technologie',
    model: 'AQWO 2',
    price: usd(24000, 'APPROXIMATE', 2022, 'Approximate US retail for the AQWO 2; French list pricing converts differently by market.'),
    year: 2021,
    country: 'France',
    architecture: 'hybrid',
    core: 'Dual-path converter handling PCM and native DSD, valve output stage',
    outputStage: 'tube',
    dynamicRangeDb: 112,
    thdPlusNDb: -84,
    crosstalkDb: -88,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 900,
    balanced: true,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    maxBitDepth: 24,
    dsd: 'dsd64',
    filters: [FILTERS.sharpLinear(128), FILTERS.slowLinear(48)],
    harmonicShape: HARMONIC_SHAPES.tubeDac,
    response: TUBE_LINE(11, -1.0),
    jitterPsRms: 55,
    slewRateVus: 10,
    confidence: 'ESTIMATED',
    accent: '#c8b273',
    tags: ['valve-output', 'sacd-lineage', 'dual-path', 'coupling-cap'],
    notes:
      'A conventional oversampled converter followed by a valve gain stage, which puts the dominant nonlinearity after conversion rather than inside it. Two things separate it from the Thrax entry despite both using valves: the converter contributes no ladder INL, so distortion falls away cleanly at low level, and the output impedance is high enough that any preamplifier input below about 20 kilohms will audibly load the low end through the coupling capacitor. The modelled response therefore carries a first-order low-frequency corner well inside the audio band, not just a nominal one.',
    provenanceNote:
      'Valve output stage, DSD path and disc/DAC feature set from Metronome published material. Metronome does not publish THD or dynamic range for this model, so those figures are estimates typical of oversampled converters with a capacitor-coupled valve stage. Output impedance and price are approximate.',
  }),

  makeDac({
    mfr: 'NAD',
    model: 'M51',
    price: usd(1999, 'ORIGINAL_RETAIL', 2012, 'US launch price of the Masters Series M51 Direct Digital DAC; now discontinued.'),
    year: 2012,
    country: 'Canada',
    architecture: 'delta-sigma',
    core: 'NAD Direct Digital 35-bit PWM modulator clocked at 108 MHz, no discrete converter chip',
    outputStage: 'op-amp',
    dynamicRangeDb: 120,
    thdPlusNDb: -106,
    crosstalkDb: -110,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 75,
    balanced: true,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    dsd: 'none',
    filters: SIMPLE_FILTERS(),
    internalOversampling: 16,
    noiseShaperOrder: 4,
    jitterPsRms: 35,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7f8c99',
    tags: ['direct-digital', 'pwm', 'masters-series', 'analog-reconstruction-filter'],
    notes:
      'Conversion happens by noise-shaping the sample stream up to a very high-rate pulse-width signal and reconstructing it with an analog low-pass, so there is no conventional current-output converter and no separate digital interpolation filter to choose between. The practical consequences modelled here are a clean, level-independent distortion floor, out-of-band noise governed by the modulator rather than by a chipset noise shaper, and a fixed reconstruction characteristic. It is also by far the cheapest entry in this group and behaves like it in one respect only: a 75-ohm output impedance rather than the sub-ohm figure a discrete class-A stage would give.',
    provenanceNote:
      'THD, signal-to-noise ratio, output level and the Direct Digital PWM architecture are from NAD published specifications for the M51. The internal oversampling ratio quoted here is an approximation of the modulator rate relative to 44.1 kHz input, and the noise-shaper order is inferred, not published.',
  }),
];
